import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getMyOrders } from "../services/orderService";
import { friendlyError } from "../api/api";
import EmptyState from "../components/ui/EmptyState";
import ErrorState from "../components/ui/ErrorState";
import ListSkeleton from "../components/ui/ListSkeleton";
import { formatBusinessDate } from "../utils/businessDate";
import { formatMoney } from "../utils/money";

export default function OrderHistory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page") || 1);

  const [status, setStatus] = useState("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1 });

  const load = useCallback(() => {
    setStatus("loading");
    getMyOrders({ page, limit: 20 })
      .then((res) => {
        setData(res);
        setStatus(res.items.length === 0 ? "empty" : "ok");
      })
      .catch((err) => {
        setErrorMessage(friendlyError(err, "Unable to load your orders."));
        setStatus("error");
      });
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const goToPage = (p) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(p));
    setSearchParams(next);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <h1 className="text-xl font-semibold text-neutral-900 mb-6">Order History</h1>

      {status === "loading" && <ListSkeleton rows={5} />}
      {status === "error" && <ErrorState message={errorMessage} onRetry={load} />}
      {status === "empty" && (
        <EmptyState title="No orders yet." note="Orders the shop records for your account will appear here. To ask for prices, use Request a Quote." />
      )}

      {status === "ok" && (
        <>
          <div className="space-y-3">
            {data.items.map((order) => (
              <Link
                key={order.id}
                to={`/orders/${order.id}`}
                className="block rounded-xl border border-neutral-200 bg-white p-4 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-neutral-900">{order.order_code}</span>
                  <span className="text-xs rounded-full px-2 py-0.5 bg-neutral-100 text-neutral-600 capitalize">
                    {order.status}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-2 text-sm text-neutral-500">
                  <span>{formatBusinessDate(order.created_at)}</span>
                  <span className="font-medium text-neutral-900">{formatMoney(order.total)}</span>
                </div>
              </Link>
            ))}
          </div>

          {data.total_pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <button
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
                className="text-sm px-3 py-1.5 rounded-lg border border-neutral-300 disabled:opacity-40 hover:bg-neutral-50"
              >
                Previous
              </button>
              <span className="text-sm text-neutral-500">
                Page {data.page} of {data.total_pages}
              </span>
              <button
                disabled={page >= data.total_pages}
                onClick={() => goToPage(page + 1)}
                className="text-sm px-3 py-1.5 rounded-lg border border-neutral-300 disabled:opacity-40 hover:bg-neutral-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
